import interLatinWoff2 from "@fontsource-variable/inter/files/inter-latin-wght-normal.woff2?url";
import { Outlet, createRootRoute, HeadContent, Scripts, useRouter, useRouterState } from "@tanstack/react-router";
import { useEffect } from "react";

import appCss from "../styles.css?url";
import { SkipToContent } from "@/components/site/SkipToContent";
import { ConsentProvider } from "@/lib/consent/ConsentProvider";
import { I18nProvider } from "@/lib/i18n/I18nProvider";
import { seoLinksForPath } from "@/lib/seo/hreflang";
import { CookieBanner } from "@/components/site/CookieBanner";
import { ErrorState } from "@/components/site/ErrorState";
import { Toaster } from "@/components/ui/sonner";
import { orgSchema, websiteSchema } from "@/lib/seo/schema";
import { DeferredThirdParty } from "@/components/site/DeferredThirdParty";
import { CtaClickTracker } from "@/components/site/CtaClickTracker";
import { FormEngagementTracker } from "@/components/site/FormEngagementTracker";
import { VisualEditDebugPanel } from "@/components/site/VisualEditDebugPanel";
import { VisualEditSelectorBridge } from "@/components/site/VisualEditSelectorBridge";
import { NotFoundPage } from "@/components/site/NotFoundPage";

const NotFoundComponent = NotFoundPage;

function RootErrorComponent({ error, reset }: { error: unknown; reset: () => void }) {
  const router = useRouter();
  console.error(error);
  return (
    <ErrorState
      detail={error instanceof Error ? error.message : String(error)}
      onRetry={() => {
        router.invalidate();
        reset();
      }}
    />
  );
}

export const Route = createRootRoute({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "Perceptica - Media Intelligence for SEE" },
      {
        name: "description",
        content:
          "Perceptica turns media noise into clarity. Track mentions, monitor sentiment, and respond to crises faster.",
      },
      { name: "author", content: "Perceptica" },
      { property: "og:site_name", content: "Perceptica" },
      { property: "og:title", content: "Perceptica - Media Intelligence for SEE" },
      {
        property: "og:description",
        content: "Track mentions, monitor sentiment, and respond to crises faster.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Perceptica - Media Intelligence for SEE" },
      { name: "twitter:description", content: "Media Intelligence that helps you lead your industry." },
    ],
    links: [
      // Warm DNS/TLS for the deferred marketing scripts without blocking the
      // initial load - they connect only when the browser is idle.
      { rel: "dns-prefetch", href: "https://js-eu1.hs-scripts.com" },
      { rel: "dns-prefetch", href: "https://snap.licdn.com" },
      { rel: "dns-prefetch", href: "https://px.ads.linkedin.com" },
      { rel: "dns-prefetch", href: "https://www.googletagmanager.com" },
      { rel: "icon", type: "image/png", sizes: "64x64", href: "/favicon.png" },
      { rel: "shortcut icon", type: "image/png", href: "/favicon.png" },
      { rel: "apple-touch-icon", sizes: "180x180", href: "/apple-touch-icon.png" },
      // Single font preload: Inter Variable (latin) drives both the H1 and body text.
      { rel: "preload", as: "font", type: "font/woff2", href: interLatinWoff2, crossOrigin: "anonymous" },
      {
        rel: "stylesheet",
        href: appCss,
      },
    ],
    scripts: [
      {
        children:
          "window.dataLayer = window.dataLayer || [];function gtag(){dataLayer.push(arguments);}gtag('consent','default',{ad_storage:'denied',ad_user_data:'denied',ad_personalization:'denied',analytics_storage:'denied',functionality_storage:'granted',security_storage:'granted',wait_for_update:500});gtag('js', new Date());",
      },
      {
        type: "text/plain",
        "data-service": "facebook",
        "data-category": "marketing",
        children:
          "!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window, document,'script','https://connect.facebook.net/en_US/fbevents.js');fbq('init', '489661155031885');fbq('track', 'PageView');",
      } as never,
      {
        type: "application/ld+json",
        children: JSON.stringify(orgSchema()),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(websiteSchema()),
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: RootErrorComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const { locale, canonical: canonicalUrl, alternates, bilingual } = seoLinksForPath(pathname);
  return (
    <html lang={locale}>
      <head>
        <HeadContent />
        <link rel="canonical" href={canonicalUrl} />
        {alternates.map((a) => (
          <link key={a.hreflang} rel="alternate" hrefLang={a.hreflang} href={a.href} />
        ))}
        <meta property="og:locale" content={locale === "bg" ? "bg_BG" : "en_GB"} />
        {bilingual && (
          <meta property="og:locale:alternate" content={locale === "bg" ? "en_GB" : "bg_BG"} />
        )}

        
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

export function RootComponent() {
  return (
    <I18nProvider>
      <ConsentProvider>
        <SkipToContent />
        <FirstVisitLocaleRedirect />
        <GaPageViewTracker />
        <DeferredThirdParty />
        <CtaClickTracker />
        <FormEngagementTracker />
        <Outlet />
        <CookieBanner />
        <Toaster richColors position="top-right" />
        <VisualEditDebugPanel />
        <VisualEditSelectorBridge />
      </ConsentProvider>
    </I18nProvider>
  );
}

function GaPageViewTracker() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const searchStr = useRouterState({ select: (s) => s.location.searchStr });
  useEffect(() => {
    if (typeof window === "undefined") return;
    const w = window as unknown as { gtag?: (...args: unknown[]) => void };
    if (typeof w.gtag !== "function") return;
    // Defer one tick so route head() updates document.title first.
    const id = window.setTimeout(() => {
      w.gtag!("event", "page_view", {
        page_path: pathname + (searchStr ? `?${searchStr}` : ""),
        page_location: window.location.href,
        page_title: document.title,
      });
    }, 0);
    return () => window.clearTimeout(id);
  }, [pathname, searchStr]);
  return null;
}

/**
 * First visit only auto-redirect to /bg for Bulgarian-speaking visitors who
 * land on "/". Runs at most once per browser tab (guarded by sessionStorage)
 * and only when the visitor has NO stored locale preference yet. Once the
 * user has interacted with the language switcher (which sets the cookie),
 * we never auto-redirect again - their explicit choice always wins.
 */
function FirstVisitLocaleRedirect() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (pathname !== "/") return;
    // Only auto-redirect once per tab session, no matter what.
    try {
      if (window.sessionStorage.getItem("perceptica_locale_redirected") === "1") return;
      window.sessionStorage.setItem("perceptica_locale_redirected", "1");
    } catch {
      // sessionStorage may be unavailable (private mode, etc.) - fall through.
    }
    const cookie = document.cookie
      .split(";")
      .map((c) => c.trim())
      .find((c) => c.startsWith("perceptica_locale="));
    const stored = cookie?.split("=")[1];
    // If the user has ANY stored preference, respect their explicit choice
    // and never auto-redirect. The locale switcher handles navigation.
    if (stored === "en" || stored === "bg") return;
    // No stored preference - sniff Accept-Language.
    const langs = navigator.languages ?? [navigator.language];
    const prefersBg = langs.some((l) => l?.toLowerCase().startsWith("bg"));
    if (prefersBg) window.location.replace("/bg");
  }, [pathname]);
  return null;
}
